/**
 * User Profile Screen (Startup Member)
 *
 * Shows the logged-in user's personal profile information.
 * Separate from Company Profile which shows the startup's company info.
 */

import { useState } from 'react';
import { View, Text, TextInput, Image, ActivityIndicator } from 'react-native';
import { useRouter } from 'expo-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth, supabase } from '@/lib/supabase';
import { rpc } from '@/lib/supabase/rpc';
import { handleError, reportError } from '@/lib/errors';
import {
  Camera,
  User,
  Mail,
  Briefcase,
  MapPin,
  Settings,
  ChevronRight,
  Save,
  Shield,
  CreditCard,
  LogOut,
  Building2,
  FileText,
} from 'lucide-react-native';
import Animated, { FadeIn, FadeInDown } from 'react-native-reanimated';
import { ScreenFrame } from '@/components/ScreenFrame';
import { useTrackScreen } from '@/lib/analytics';
import { useThemeColors } from '@/lib/useThemeColors';
import { AppLayout } from '@/components/AppLayout';
import { SectionDivider } from '@/components/SectionDivider';
import { useInverioStore } from '@/lib/state/inverio-store';
import { useProfileStore } from '@/lib/state/profile-store';
import { useMyStartup } from '@/lib/startup/use-my-startup';
import { useFilePicker, useFileUpload } from '@/lib/storage/hooks';
import { useAlert } from '@/components/AlertDialog';
import { ProfileCompletenessBadge } from '@/components/ProfileCompleteness';
import { capitalizeWords } from '@/lib/format';
import AnimatedPressable from '@/components/AnimatedPressable';
import { KeyboardAwareBody } from '@/components/modal/KeyboardAwareBody';
import { PROFILE_PLANS_LINK } from '@/lib/commerce/navigation';

// Founder STATUS (relationship to the company) is distinct from company FUNCTION
// (CEO/COO/…). Both are captured in onboarding on team_members; the profile
// cascades that distinction — status is read-only (ownership-linked, set at
// onboarding/claim), function is the field the person edits about themselves.
const FOUNDER_STATUS_LABELS: Record<string, string> = {
  founder: 'Founder',
  co_founder: 'Co-founder',
  team_member: 'Team member',
};

export default function StartupUserProfileScreen() {
  useTrackScreen('UserProfile');
  const router = useRouter();
  const { signOut, user: authUser, refreshProfile } = useAuth();
  const colors = useThemeColors();
  const { alert, success, confirmDestructive } = useAlert();
  const queryClient = useQueryClient();

  const user = useProfileStore((s) => s.user);
  const setUser = useProfileStore((s) => s.setUser);
  const startupProfile = useProfileStore((s) => s.startupProfile);
  const reset = useInverioStore((s) => s.reset);

  const { startupId } = useMyStartup();

  // The caller's membership on THEIR startup — founder_status (read-only
  // relationship) + function_title (editable company function). Scoped to the
  // current startup: a person may be a member of several with different titles.
  const membershipKey = ['my-membership', startupId, authUser?.id] as const;
  const { data: membership } = useQuery({
    queryKey: membershipKey,
    enabled: !!startupId && !!authUser?.id,
    queryFn: async () => {
      const { data: team } = await supabase
        .from('teams')
        .select('id')
        .eq('startup_id', startupId!)
        .maybeSingle();
      if (!team) return null;
      const { data } = await supabase
        .from('team_members')
        .select('founder_status, function_title')
        .eq('team_id', team.id)
        .eq('user_id', authUser!.id)
        .eq('status', 'active')
        .maybeSingle();
      return data;
    },
  });

  const founderStatusLabel = membership?.founder_status
    ? FOUNDER_STATUS_LABELS[membership.founder_status] ?? membership.founder_status
    : null;

  // Form state
  const [isEditing, setIsEditing] = useState(false);
  const [fullName, setFullName] = useState(user?.name || '');
  const [functionTitle, setFunctionTitle] = useState('');
  const [location, setLocation] = useState(user?.location || '');
  const [bio, setBio] = useState(user?.bio || '');
  const [isSaving, setIsSaving] = useState(false);

  // Enter edit mode with all fields seeded from the current source of truth.
  const beginEdit = () => {
    setFullName(user?.name || '');
    setLocation(user?.location || '');
    setBio(user?.bio || '');
    setFunctionTitle(membership?.function_title ?? '');
    setIsEditing(true);
  };

  // Avatar upload — pick a square image, upload to the public `avatars` bucket
  // under the caller's own folder (RLS-scoped), then persist the public URL to
  // profiles.avatar_url so it survives reload and shows everywhere.
  const { pickImage } = useFilePicker({ bucket: 'avatars' });
  const { upload, isUploading } = useFileUpload({ bucket: 'avatars', path: authUser?.id });

  const handleChangeAvatar = async () => {
    if (isUploading || !authUser?.id) return;
    try {
      const picked = await pickImage();
      if (!picked) return; // cancelled

      const result = await upload(picked, { upsert: true });
      if (!result.success || !result.publicUrl) {
        alert("Couldn't upload photo", result.error || 'Upload failed. Please try again.');
        return;
      }

      const { error: updateError } = await supabase
        .from('profiles')
        .update({ avatar_url: result.publicUrl })
        .eq('id', authUser.id);
      if (updateError) throw updateError;

      setUser({ ...user, name: user?.name || '', email: user?.email || '', avatarUrl: result.publicUrl });
      await refreshProfile();
    } catch (err) {
      const { message, isCancelled } = handleError(err, {
        context: 'StartupUserProfile.handleChangeAvatar',
      });
      if (!isCancelled) alert("Couldn't update photo", message);
    }
  };

  const handleSave = async () => {
    if (!fullName.trim()) {
      alert('Required Field', 'Please enter your full name.');
      return;
    }

    setIsSaving(true);
    try {
      const finalName = capitalizeWords(fullName.trim());
      const parts = finalName.split(' ');
      const firstName = parts[0] || '';
      const lastName = parts.slice(1).join(' ');
      const trimmedBio = bio.trim();
      const trimmedLocation = location.trim();
      const trimmedFunction = functionTitle.trim();

      if (authUser?.id) {
        const { error: profileError } = await supabase
          .from('profiles')
          .update({
            first_name: firstName || null,
            last_name: lastName || null,
            bio: trimmedBio || null,
            location: trimmedLocation || null,
          })
          .eq('id', authUser.id);
        if (profileError) throw profileError;

        // Company function → the caller's own team_members row via a
        // column-scoped RPC (safe for non-owner members). Only when a startup
        // is resolved.
        if (startupId) {
          const { error: fnError } = await rpc('update_my_function_title', {
            p_startup_id: startupId,
            p_function_title: trimmedFunction,
          });
          if (fnError) throw fnError;
        }

        await refreshProfile();
        await queryClient.invalidateQueries({ queryKey: membershipKey });
      }

      // Optimistic store update so the change is instant.
      setUser({
        ...user,
        name: finalName,
        email: user?.email || authUser?.email || '',
        bio: trimmedBio || undefined,
        location: trimmedLocation || undefined,
      });

      setIsEditing(false);
      success('Profile Updated', 'Your profile has been saved.');
    } catch (err) {
      const { message, isCancelled } = handleError(err, {
        context: 'StartupUserProfile.handleSave',
      });
      if (!isCancelled) alert("Couldn't save", message);
    } finally {
      setIsSaving(false);
    }
  };

  const handleLogout = async () => {
    const confirmed = await confirmDestructive(
      'Sign Out',
      'Are you sure you want to sign out?',
      'Sign Out'
    );
    if (!confirmed) return;
    try {
      // Single source of truth: revokes the Supabase session + resets all
      // user-scoped stores. Without this the session survives and the user is
      // bounced straight back into the app (the native sign-out bug, 2026-06-13).
      await signOut();
      reset(); // belt-and-suspenders store reset
    } catch (error) {
      reportError(error, { context: 'StartupUserProfile.handleLogout' });
    } finally {
      router.replace('/login');
    }
  };

  return (
    <AppLayout title="My Profile">
      {/* Profile is now reached from the top-right avatar rather than a tab, so
         it is a SECONDARY page and carries a back control. The fallback matters:
         the avatar can be pressed from any screen, and a deep link into the
         profile has no history at all. */}
      <ScreenFrame
        title="My Profile"
        back={{ fallback: '/(startup)/startup-dashboard' }}
      >
        <KeyboardAwareBody
          className="flex-1"
          style={{ backgroundColor: colors.surface.secondary }}
          showsVerticalScrollIndicator={false}
          contentContainerStyle={{ paddingBottom: 100 }}
        >
        {/* Profile Header */}
        <Animated.View
          entering={FadeIn.duration(400)}
          className="items-center pt-6 pb-8 px-6"
          style={{ backgroundColor: colors.surface.primary }}
        >
          {/* Avatar */}
          <View className="relative mb-4">
            {user?.avatarUrl ? (
              <View
                className="w-24 h-24 rounded-full overflow-hidden"
                style={{ borderWidth: 3, borderColor: colors.primary[500] }}
              >
                <Image
                  source={{ uri: user.avatarUrl }}
                  style={{ width: '100%', height: '100%' }}
                  resizeMode="cover"
                />
              </View>
            ) : (
              <View
                className="w-24 h-24 rounded-full items-center justify-center"
                style={{ backgroundColor: colors.primary[100] }}
              >
                <Text style={{ color: colors.primary[700] }} className="text-3xl font-bold">
                  {(user?.name || 'U').charAt(0).toUpperCase()}
                </Text>
              </View>
            )}
            <AnimatedPressable
              onPress={handleChangeAvatar}
              disabled={isUploading}
              accessibilityLabel="Change profile photo"
              className="absolute bottom-0 right-0 w-8 h-8 rounded-full items-center justify-center"
              style={{ backgroundColor: colors.primary[600] }}
            >
              {isUploading ? (
                <ActivityIndicator size="small" color="white" />
              ) : (
                <Camera size={16} color="white" />
              )}
            </AnimatedPressable>
          </View>

          <Text style={{ color: colors.text.primary }} className="text-xl font-bold">
            {user?.name || 'User'}
          </Text>
          <Text style={{ color: colors.text.secondary }} className="text-sm mt-1" numberOfLines={1}>
            {[membership?.function_title, founderStatusLabel].filter(Boolean).join(' · ') || 'Founder'} at {startupProfile?.name || 'Startup'}
          </Text>

          <View className="mt-3">
            <ProfileCompletenessBadge />
          </View>
        </Animated.View>

        <View className="p-5 gap-6">
          {/* Personal Info Section */}
          <Animated.View entering={FadeInDown.delay(100).duration(400)}>
            <View className="flex-row items-center mb-3" style={{ gap: 12 }}>
              <Text
                style={{
                  color: colors.text.primary,
                  fontSize: 12,
                  fontWeight: '700',
                  letterSpacing: 1.4,
                  textTransform: 'uppercase',
                  flexShrink: 0,
                }}
                numberOfLines={1}
              >
                Personal Information
              </Text>
              <View style={{ flex: 1, height: 1, backgroundColor: colors.border.dark }} />
              <AnimatedPressable
                onPress={() => (isEditing ? setIsEditing(false) : beginEdit())}
                className="px-3 py-1.5 rounded-lg"
                style={{ backgroundColor: colors.primary[50], flexShrink: 0 }}
              >
                <Text style={{ color: colors.primary[600] }} className="text-sm font-medium">
                  {isEditing ? 'Cancel' : 'Edit'}
                </Text>
              </AnimatedPressable>
            </View>

            <View
              className="rounded-2xl overflow-hidden"
              style={{
                backgroundColor: colors.surface.primary,
                borderWidth: 1,
                borderColor: colors.border.light,
              }}
            >
              {/* Full Name */}
              <View className="flex-row items-center px-4 py-4">
                <View
                  className="w-10 h-10 rounded-full items-center justify-center mr-3"
                  style={{ backgroundColor: colors.surface.secondary }}
                >
                  <User size={20} color={colors.text.tertiary} />
                </View>
                <View className="flex-1">
                  <Text style={{ color: colors.text.tertiary }} className="text-xs mb-1">
                    Full Name
                  </Text>
                  {isEditing ? (
                    <TextInput
                      style={{ color: colors.text.primary }}
                      className="text-base font-medium py-0"
                      value={fullName}
                      onChangeText={setFullName}
                      placeholder="Enter your name"
                      placeholderTextColor={colors.text.tertiary}
                    />
                  ) : (
                    <Text style={{ color: colors.text.primary }} className="text-base font-medium">
                      {user?.name || 'Not set'}
                    </Text>
                  )}
                </View>
              </View>

              <View className="mx-4" style={{ height: 1, backgroundColor: colors.border.light }} />

              {/* Email */}
              <View className="flex-row items-center px-4 py-4">
                <View
                  className="w-10 h-10 rounded-full items-center justify-center mr-3"
                  style={{ backgroundColor: colors.surface.secondary }}
                >
                  <Mail size={20} color={colors.text.tertiary} />
                </View>
                <View className="flex-1">
                  <Text style={{ color: colors.text.tertiary }} className="text-xs mb-1">
                    Email
                  </Text>
                  {/* Email is managed by the auth provider — read-only here. */}
                  <Text style={{ color: colors.text.primary }} className="text-base font-medium" numberOfLines={1}>
                    {user?.email || 'Not set'}
                  </Text>
                </View>
              </View>

              <View className="mx-4" style={{ height: 1, backgroundColor: colors.border.light }} />

              {/* Founder status — read-only relationship (founder / co-founder /
                  team member), set at onboarding/claim. Distinct from function. */}
              {founderStatusLabel && (
                <>
                  <View className="flex-row items-center px-4 py-4">
                    <View
                      className="w-10 h-10 rounded-full items-center justify-center mr-3"
                      style={{ backgroundColor: colors.surface.secondary }}
                    >
                      <Shield size={20} color={colors.text.tertiary} />
                    </View>
                    <View className="flex-1">
                      <Text style={{ color: colors.text.tertiary }} className="text-xs mb-1">
                        Founder status
                      </Text>
                      <Text style={{ color: colors.text.primary }} className="text-base font-medium" numberOfLines={1}>
                        {founderStatusLabel}
                      </Text>
                    </View>
                  </View>
                  <View className="mx-4" style={{ height: 1, backgroundColor: colors.border.light }} />
                </>
              )}

              {/* Company function — the person's job function (CEO/COO/…), editable. */}
              <View className="flex-row items-center px-4 py-4">
                <View
                  className="w-10 h-10 rounded-full items-center justify-center mr-3"
                  style={{ backgroundColor: colors.surface.secondary }}
                >
                  <Briefcase size={20} color={colors.text.tertiary} />
                </View>
                <View className="flex-1">
                  <Text style={{ color: colors.text.tertiary }} className="text-xs mb-1">
                    Company function
                  </Text>
                  {isEditing ? (
                    <TextInput
                      style={{ color: colors.text.primary }}
                      className="text-base font-medium py-0"
                      value={functionTitle}
                      onChangeText={setFunctionTitle}
                      placeholder="e.g. CEO, COO, CTO"
                      placeholderTextColor={colors.text.tertiary}
                    />
                  ) : (
                    <Text
                      style={{ color: membership?.function_title ? colors.text.primary : colors.text.tertiary }}
                      className="text-base font-medium"
                      numberOfLines={1}
                    >
                      {membership?.function_title || 'Not set'}
                    </Text>
                  )}
                </View>
              </View>

              <View className="mx-4" style={{ height: 1, backgroundColor: colors.border.light }} />

              {/* Location — editable personal location. */}
              <View className="flex-row items-center px-4 py-4">
                <View
                  className="w-10 h-10 rounded-full items-center justify-center mr-3"
                  style={{ backgroundColor: colors.surface.secondary }}
                >
                  <MapPin size={20} color={colors.text.tertiary} />
                </View>
                <View className="flex-1">
                  <Text style={{ color: colors.text.tertiary }} className="text-xs mb-1">
                    Location
                  </Text>
                  {isEditing ? (
                    <TextInput
                      style={{ color: colors.text.primary }}
                      className="text-base font-medium py-0"
                      value={location}
                      onChangeText={setLocation}
                      placeholder="City, Country"
                      placeholderTextColor={colors.text.tertiary}
                    />
                  ) : (
                    <Text
                      style={{ color: user?.location ? colors.text.primary : colors.text.tertiary }}
                      className="text-base font-medium"
                      numberOfLines={1}
                    >
                      {user?.location || 'Not set'}
                    </Text>
                  )}
                </View>
              </View>

              <View className="mx-4" style={{ height: 1, backgroundColor: colors.border.light }} />

              {/* Bio / About */}
              <View className="px-4 py-4">
                <View className="flex-row items-start">
                  <View
                    className="w-10 h-10 rounded-full items-center justify-center mr-3"
                    style={{ backgroundColor: colors.surface.secondary }}
                  >
                    <FileText size={20} color={colors.text.tertiary} />
                  </View>
                  <View className="flex-1">
                    <Text style={{ color: colors.text.tertiary }} className="text-xs mb-1">
                      About Me
                    </Text>
                    {isEditing ? (
                      <TextInput
                        style={{
                          color: colors.text.primary,
                          backgroundColor: colors.surface.secondary,
                          borderRadius: 8,
                          padding: 12,
                          minHeight: 80,
                          textAlignVertical: 'top',
                        }}
                        value={bio}
                        onChangeText={setBio}
                        placeholder="Write a brief bio about yourself..."
                        placeholderTextColor={colors.text.tertiary}
                        multiline
                        numberOfLines={4}
                      />
                    ) : (
                      <Text
                        style={{ color: user?.bio ? colors.text.primary : colors.text.tertiary }}
                        className="text-base"
                      >
                        {user?.bio || 'No bio added yet. Tap Edit to add a brief introduction about yourself.'}
                      </Text>
                    )}
                  </View>
                </View>
              </View>
            </View>

            {/* Save Button */}
            {isEditing && (
              <AnimatedPressable
                onPress={handleSave}
                disabled={isSaving}
                className="flex-row items-center justify-center gap-2 py-4 rounded-xl mt-4"
                style={{ backgroundColor: colors.primary[600] }}
              >
                <Save size={20} color="white" />
                <Text className="text-white font-semibold">
                  {isSaving ? 'Saving...' : 'Save Changes'}
                </Text>
              </AnimatedPressable>
            )}
          </Animated.View>

          {/* Quick Links */}
          <Animated.View entering={FadeInDown.delay(200).duration(400)}>
            <SectionDivider label="Quick Links" marginTop={0} marginBottom={12} />

            <View
              className="rounded-2xl overflow-hidden"
              style={{
                backgroundColor: colors.surface.primary,
                borderWidth: 1,
                borderColor: colors.border.light,
              }}
            >
              <AnimatedPressable
                onPress={() => router.push('/(startup)/company')}
                className="flex-row items-center px-4 py-4"
              >
                <View
                  className="w-10 h-10 rounded-full items-center justify-center mr-3"
                  style={{ backgroundColor: colors.primary[50] }}
                >
                  <Building2 size={20} color={colors.primary[600]} />
                </View>
                <View className="flex-1">
                  <Text style={{ color: colors.text.primary }} className="font-medium">
                    Company Profile
                  </Text>
                  <Text style={{ color: colors.text.tertiary }} className="text-xs">
                    Edit startup information
                  </Text>
                </View>
                <ChevronRight size={20} color={colors.text.tertiary} />
              </AnimatedPressable>

              {PROFILE_PLANS_LINK && (
                <>
                  <View className="mx-4" style={{ height: 1, backgroundColor: colors.border.light }} />

                  <AnimatedPressable
                    onPress={() => PROFILE_PLANS_LINK!.open(router, 'startup')}
                    className="flex-row items-center px-4 py-4"
                  >
                    <View
                      className="w-10 h-10 rounded-full items-center justify-center mr-3"
                      style={{ backgroundColor: colors.amber.light }}
                    >
                      <CreditCard size={20} color={colors.warning} />
                    </View>
                    <View className="flex-1">
                      <Text numberOfLines={1} style={{ color: colors.text.primary }} className="font-medium">
                        {PROFILE_PLANS_LINK.label}
                      </Text>
                      <Text style={{ color: colors.text.tertiary }} className="text-xs">
                        Manage your plan
                      </Text>
                    </View>
                    <ChevronRight size={20} color={colors.text.tertiary} />
                  </AnimatedPressable>
                </>
              )}

              <View className="mx-4" style={{ height: 1, backgroundColor: colors.border.light }} />

              <AnimatedPressable
                onPress={() => router.push('/settings')}
                className="flex-row items-center px-4 py-4"
              >
                <View
                  className="w-10 h-10 rounded-full items-center justify-center mr-3"
                  style={{ backgroundColor: colors.surface.secondary }}
                >
                  <Settings size={20} color={colors.text.tertiary} />
                </View>
                <View className="flex-1">
                  <Text style={{ color: colors.text.primary }} className="font-medium">
                    Settings
                  </Text>
                  <Text style={{ color: colors.text.tertiary }} className="text-xs">
                    App preferences
                  </Text>
                </View>
                <ChevronRight size={20} color={colors.text.tertiary} />
              </AnimatedPressable>
            </View>
          </Animated.View>

          {/* Sign Out */}
          <Animated.View entering={FadeInDown.delay(300).duration(400)}>
            <AnimatedPressable
              onPress={handleLogout}
              className="flex-row items-center justify-center gap-2 py-4 rounded-xl"
              style={{ backgroundColor: colors.red.light }}
            >
              <LogOut size={20} color={colors.error} />
              <Text style={{ color: colors.error }} className="font-semibold">
                Sign Out
              </Text>
            </AnimatedPressable>
          </Animated.View>
        </View>
        </KeyboardAwareBody>
      </ScreenFrame>
    </AppLayout>
  );
}
